"use client";

import React from "react";
import { Send, Loader, ArrowRight, MessageSquare } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center min-h-[450px] text-center p-8 animate-in fade-in zoom-in duration-500">
        <div className="w-20 h-20 rounded-full bg-secondary/10 flex items-center justify-center text-secondary mb-6">
          <Send className="w-10 h-10" />
        </div>
        <h3 className="text-3xl font-bold text-primary mb-4 tracking-tight">Message Received</h3>
        <p className="text-muted-foreground max-w-sm mx-auto mb-8 text-lg">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="rounded-full px-8 py-6 border-secondary text-secondary hover:bg-secondary/5">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary font-semibold">Full Name</FormLabel>
              <FormControl>
                <input {...field} placeholder="Jane Doe" className="w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground focus:border-secondary focus:ring-2 focus:ring-secondary/20 outline-none transition-all" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary font-semibold">Email Address</FormLabel>
              <FormControl>
                <input {...field} type="email" placeholder="jane@example.com" className="w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground focus:border-secondary focus:ring-2 focus:ring-secondary/20 outline-none transition-all" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-primary font-semibold">Phone Number (Optional)</FormLabel>
            <FormControl>
              <input {...field} type="tel" placeholder="+1 (407) 555-0123" className="w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground focus:border-secondary focus:ring-2 focus:ring-secondary/20 outline-none transition-all" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Message must be at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-primary font-semibold">How can we help?</FormLabel>
            <FormControl>
              <textarea {...field} rows={4} placeholder="Tell us about your property or rental needs..." className="w-full bg-background border border-border rounded-lg px-4 py-3 text-foreground focus:border-secondary focus:ring-2 focus:ring-secondary/20 outline-none resize-none transition-all" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <p className="text-destructive text-sm font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-secondary text-white hover:bg-secondary-600 rounded-lg py-7 text-lg font-bold shadow-lg shadow-secondary/20 transition-all group">
        {isSubmitting ? <>
            <Loader className="mr-2 h-5 w-5 animate-spin" />
            Sending Inquiry...
          </> : <>
            Send Message
            <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
          </>}
      </Button>
    </Form>;
}
export default function BeginTheDialogue() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="lets-talk" ref={ref} className="relative w-full overflow-hidden bg-background transition-opacity duration-1000 opacity-100 ![padding:25px_0px_0px_0px]">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-secondary/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/10 text-secondary font-bold text-sm uppercase tracking-widest mb-6">
              <MessageSquare className="w-4 h-4" />
              Get In Touch
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4 tracking-tight">Start the Conversation</h2>
          </div>

          <div className="w-full">
            <div className="bg-white/80 backdrop-blur-md border border-border rounded-2xl p-8 md:p-12 shadow-2xl shadow-primary/5">
              <FormProvider formKey="letstalk_form_contact" formId="letstalk_form_contact" sectionName="lets-talk" defaultValues={{
              name: "",
              email: "",
              phone: "",
              message: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              message: {
                required: true
              }
            }} submitText="Send Message" successMessage="Thank you! Our team will review your inquiry and reach out within one business day." errorMessage="Something went wrong. Please try again or call us directly." className="w-full">
                <ContactForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}